import React, { lazy, Suspense, useEffect } from 'react';
import { Switch, Route, Redirect, useParams, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import browserHistory from '../history';
import { urls } from './Urls';
import { useDispatch, useSelector } from 'react-redux';
import { verificationPendingActions } from '../Store/Slices/VerificationPendingSlice';
import { isUserLoggedIn } from '../Util';
import Home from '../Modules/Home/Home';
import SkeletonLoader from '../Components/Loader/SkeletonLoader';

// Lazy loaded components
const LoginComponent = lazy(() => import('../Modules/Login/Login'));
const ForgotPassword = lazy(() => import('../Modules/ForgotPassword/ForgotPassword'));
const SignUp = lazy(() => import('../Modules/SignUp/SignUp'));
const ContactUs = lazy(() => import('../Modules/ContactUs/ContactUS'));
const FAQ = lazy(() => import('../Modules/FAQ/Faq'));
const Listing = lazy(() => import('../Modules/ListingPage/Listing'));
const ProductDetail = lazy(() => import('../Modules/ProductDetail/ProductDetail'));
const Viewer = lazy(() => import('../Modules/WebGLViewer/Viewer'));
const Editor = lazy(() => import('../Modules/Editor/Editor'));
const Customization = lazy(() => import('../Modules/Customization/Customization'));
const Services = lazy(() => import('../Modules/Services/Services'));
const MyCart = lazy(() => import('../Modules/MyCart/MyCart'));
const BookRestoration = lazy(() => import('../Modules/Services/BookRestoration/BookRestoration'));
const PDFPrinting = lazy(() => import('../Modules/Services/PDFPrinting/PDFPrinting'));
const ThesisBinding = lazy(() => import('../Modules/Services/ThesisBinding/ThesisBinding'));
const LeatherBinding = lazy(() => import('../Modules/Services/LeatherBinding/LeatherBinding'));
const OurCompany = lazy(() => import('../Modules/OurCompany/OurCompany'));
const MyAccount = lazy(() => import('../Modules/MyAccount/MyAccount'));
const RBExclusive = lazy(() => import('../Modules/RBExclusive/RBExclusive'));
const ForPublishers = lazy(() => import('../Modules/Services/ForPulishers/ForPublishers'));
const BecomePartner = lazy(() => import('../Modules/Services/BecomePartner/BecomePartner'));
const ForAuthors = lazy(() => import('../Modules/Services/ForAuthors/ForAuthors'));
const PrintOnDemand = lazy(() => import('../Modules/Services/PrintOnDemand/PrintOnDemand'));
const CorporateGifts = lazy(() => import('../Modules/Services/CorporateGifts/CorporateGifts'));
const Checkout = lazy(() => import('../Modules/Checkout/Checkout'));
const ThankYou = lazy(() => import('../Modules/Checkout/ThankYou/ThankYou'));
const Blog = lazy(() => import('../Modules/Blogs/Blog/Blog'));
const Blogs = lazy(() => import('../Modules/Blogs/Blogs'));
const BestSeller = lazy(() => import('../Modules/BestSeller/BestSeller'));
const BestSellerPDP = lazy(() => import('../Modules/ProductDetail/BestSellerPDP/BestSellerPDP'));
const PrivacyPolicy = lazy(() => import('../Modules/PrivacyPolicy/PrivacyPolicy'));
const LimitedEdition = lazy(() => import('../Modules/LimitedEdition/LimitedEdition'));
const ReturnRefunds = lazy(() => import('../Modules/ReturnRefunds/ReturnRefunds'));
const CreatePassword = lazy(() => import('../Modules/CreatePassword/CreatePassword'));

const GoogleAuthProvider = lazy(() => import("../Components/GoogleAuthProvider"));

const MyRouter = () => {
    const currentUser = useSelector((state) => state.user.currentUser);
    const dispatch = useDispatch()

    // Add this effect to force re-render when location changes via browser history
    useEffect(() => {
        // Set up a listener for history changes
        const unlisten = browserHistory.listen(() => {
            // Force a re-render by dispatching an action
            // This could be a custom action in your Redux store
            // Or you can use the existing action if appropriate
            // Alternatively, we can force scroll which causes React to update DOM
            window.scrollTo(0, 0);
        });
        
        // Clean up the listener when component unmounts
        return () => {
            unlisten();
        };
    }, []);

    const Protected = ({ routeProps, children }) => { 
        if (isUserLoggedIn() || currentUser?.id) {
            if(currentUser?.isVerified === false) {
                dispatch(verificationPendingActions.showMessage())
                return (
                <Redirect
                to={{
                pathname: urls.HOME,
                state: {
                    from: routeProps.location.pathname,
                },
                }}
            />)

            } else {
                return children;
            }
        } else {
            return (
            <Redirect
                to={{
                pathname: urls.LOGIN,
                state: {
                    from: routeProps.location.pathname,
                },
                }}
            />
            );
        }
    };

    const BookDetailRedirect = ({name}) => {
        const location = useLocation();
        const params = useParams();
        const searchParams = new URLSearchParams(location.search);
        const id = searchParams.get('id');
        const title = params.name

        if (id) {
          return <Redirect to={`/listing/product/${id}/null`} />;
        } else if(name === "bestSeller" && title) {
            return <Redirect to={`/product/null/${title}`} />
        }
        
        // Fallback if no ID is found
        return <Redirect to="/" />;
      };


    return (
            <Switch>
                <Route path="/best-seller-books/:name">
                    <BookDetailRedirect name={"bestSeller"} />
                </Route>
                <Route exact path={`${urls.HOME}`}>
                    <Home />
                </Route>
                <Route exact path={`${urls.SIGNUP}`}>
                    <Suspense fallback={<SkeletonLoader />}>
                        <GoogleAuthProvider>
                            <SignUp />
                        </GoogleAuthProvider>
                    </Suspense>
                </Route>
                <Route exact path={`${urls.LOGIN}`}>
                    <Suspense fallback={<SkeletonLoader />}>
                        <GoogleAuthProvider>
                            <LoginComponent />
                        </GoogleAuthProvider>
                    </Suspense>
                </Route>
                <Route exact path={`${urls.FORGOTPASSWORD}`}>
                    <ForgotPassword />
                </Route>
                <Route exact path={`${urls.CREATEPASSWORD}`}>
                    <CreatePassword />
                </Route>
                <Route exact path={`${urls.CONTACTUS}`}>
                    <ContactUs />
                </Route>
                <Route
                    path={urls.USERPROFILE}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <MyAccount />
                        </Protected>
                    )}
                />
                <Route exact path={`${urls.FAQ}`}>
                    <FAQ />
                </Route>
                <Route exact path={`${urls.LISTING}`}>
                    <Listing />
                </Route>
                <Route path="/best-seller-books">
                    <Redirect to={urls.BESTSELLER} />
                </Route>
                <Route  path={urls.BESTSELLER}>
                    <BestSeller />
                </Route>
                <Route path={`${urls.BESTSELLERPDP}/:id/:name`}>
                    <BestSellerPDP />
                </Route>
                <Route path={`${urls.BESTSELLERPDP}/:name`}>
                    <BestSellerPDP />
                </Route>
                <Route path={`${urls.VIEWBLOG}/:slug`}>
                    <Blog />
                </Route>
                <Route path={`${urls.BLOG}`}>
                    <Blogs />
                </Route>
                <Route exact path="/books/detail">
                    <BookDetailRedirect />
                </Route>
                <Route exact path={`${urls.PRODUCTDETAIL}/:id/:name`}>
                    <ProductDetail />
                </Route>
                <Route exact path={`${urls.VIEWER}`}>
                    <Viewer />
                </Route>
                <Route exact path={urls.EDITOR}>
                    <Editor />
                </Route>
                <Route
                    path={`${urls.CUSTOMIZATION}/:id/:name`}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <Customization />
                        </Protected>
                    )}
                />
                <Route exact path={urls.SERVICES}>
                    <Services />
                </Route>

                <Route path="/customized-books">
                    <Redirect to={urls.SERVICES} />
                </Route>
                <Route exact path={urls.BOOKRESTORATION}>
                    <BookRestoration />
                </Route>
                <Route exact path={urls.PDFPRINTING}>
                    <PDFPrinting />
                </Route>
                <Route path="/print-books-from-pdf">
                    <Redirect to={urls.PDFPRINTING} />
                </Route>
                <Route exact path={urls.THESISBINDING}>
                    <ThesisBinding />
                </Route>
                <Route exact path={urls.LEATHERBINDING}>
                    <LeatherBinding />
                </Route>
                <Route path="/site/leather-book-binding-services">
                    <Redirect to={urls.LEATHERBINDING} />
                </Route>
                <Route exact path={urls.CART}>
                    <MyCart />
                </Route>
                <Route
                    path={urls.CHECKOUT}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <Checkout />
                        </Protected>
                    )}
                />
                <Route
                    path={urls.CHECKOUT_THANK_YOU}
                    render={(routeProps) => (
                        <Protected routeProps={routeProps}>
                          <ThankYou />
                        </Protected>
                    )}
                />
                <Route exact path={urls.OURCOMPANY}>
                    <OurCompany />
                </Route>
                <Route exact path={urls.PRIVACYPOLICY}>
                    <PrivacyPolicy />
                </Route>
                <Route exact path={urls.LIMITEDEDITION}>
                    <LimitedEdition />
                </Route>
                <Route exact path={urls.RETURNREFUNDS}>
                    <ReturnRefunds />
                </Route>
                <Route exact path={urls.RB_EXCLUSIVE}>
                    <RBExclusive/>
                </Route>
                <Route exact path={urls.FORPUBLISHERS}>
                    <ForPublishers/>
                </Route>
                <Route exact path={urls.BECOMEPARTNER}>
                    <BecomePartner/>
                </Route>
                <Route exact path={urls.FORAUTHOR}>
                    <ForAuthors/>
                </Route>
                <Route exact path={urls.PRINTONDEMAND}>
                    <PrintOnDemand/>
                </Route>
                <Route exact path={urls.CORPORATEGIFTS}>
                    <CorporateGifts />
                </Route>
                
            </Switch>
    )
}

export default MyRouter